/**
 * The simulation playback clock: a plain mutable object, not React state.
 *
 * Once a run completes, the worker hands back the *entire* flight as dense
 * columnar arrays (see `telemetry-series.ts`) — playing it back is just
 * advancing a virtual time value and letting charts index into data that
 * already exists. Keeping that value outside Zustand/React means the 10
 * chart panels can poll it on every animation frame and call `uplot.setData`
 * directly, without a single React re-render per frame; components that do
 * need to *react* to the playhead (the Timeline scrubber, the status bar)
 * poll it at a throttled rate instead — see `useThrottledPlayhead`.
 */
export interface PlaybackClock {
  /** Mission elapsed time, seconds. */
  time: number;
  /** Length of the loaded run, seconds. Zero when nothing is loaded. */
  duration: number;
  playing: boolean;
  /** Playback rate as a multiple of wall-clock time. */
  speed: number;
}

export const playbackClock: PlaybackClock = {
  time: 0,
  duration: 0,
  playing: false,
  speed: 1,
};

let rafHandle: number | null = null;
let lastWallTimeMs: number | null = null;
let visibilityListenerInstalled = false;
const listeners = new Set<() => void>();
const MAX_WALL_STEP_SECONDS = 1;

function emitPlaybackChange(): void {
  for (const listener of listeners) listener();
}

/**
 * Advance virtual time while rejecting a large catch-up jump after a hidden
 * tab, suspended laptop, debugger pause, or overloaded frame.
 */
export function advancePlaybackByWallTime(deltaWallSeconds: number): void {
  if (!playbackClock.playing || playbackClock.duration <= 0) return;
  const safeDelta = Math.min(
    MAX_WALL_STEP_SECONDS,
    Math.max(0, Number.isFinite(deltaWallSeconds) ? deltaWallSeconds : 0),
  );
  const next = playbackClock.time + safeDelta * playbackClock.speed;
  if (next >= playbackClock.duration) {
    playbackClock.time = playbackClock.duration;
    playbackClock.playing = false;
  } else {
    playbackClock.time = Math.max(0, next);
  }
  emitPlaybackChange();
}

function tick(nowMs: number): void {
  if (lastWallTimeMs !== null && playbackClock.playing) {
    advancePlaybackByWallTime((nowMs - lastWallTimeMs) / 1000);
  }
  lastWallTimeMs = nowMs;
  rafHandle = requestAnimationFrame(tick);
}

/** Idempotent — safe to call from every chart's mount effect. */
export function ensurePlaybackClockRunning(): void {
  if (rafHandle !== null) return;
  if (!visibilityListenerInstalled && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      // Browsers pause or heavily throttle RAF in background tabs. Resume from
      // a fresh wall-clock origin instead of skipping hours of mission time.
      lastWallTimeMs = null;
    });
    visibilityListenerInstalled = true;
  }
  lastWallTimeMs = null;
  rafHandle = requestAnimationFrame(tick);
}

/**
 * Lets imperative visualizations redraw only while playback actually changes.
 * This keeps the high-quality 3D scene idle between interactions instead of
 * burning a full render loop behind otherwise static workspace panels.
 */
export function subscribePlaybackClock(listener: () => void): () => void {
  listeners.add(listener);
  ensurePlaybackClockRunning();
  return () => listeners.delete(listener);
}

export function loadRun(durationSeconds: number): void {
  playbackClock.duration = durationSeconds;
  playbackClock.time = 0;
  playbackClock.playing = false;
  // A one-to-one wall-clock replay is useful for ascent but unusable for a
  // multi-day/month transfer. Pick a readable initial rate; users can still
  // override it from the timeline.
  playbackClock.speed =
    durationSeconds > 30 * 86400
      ? 86400
      : durationSeconds > 2 * 86400
        ? 3600
        : durationSeconds > 6 * 3600
          ? 3600
          : 1;
  emitPlaybackChange();
}

export function play(): void {
  if (playbackClock.duration <= 0) return;
  if (playbackClock.time >= playbackClock.duration) playbackClock.time = 0;
  playbackClock.playing = true;
  emitPlaybackChange();
}

export function pause(): void {
  playbackClock.playing = false;
  emitPlaybackChange();
}

export function togglePlaying(): void {
  if (playbackClock.playing) pause();
  else play();
}

export function setSpeed(speed: number): void {
  if (!Number.isFinite(speed) || speed <= 0) return;
  playbackClock.speed = speed;
  emitPlaybackChange();
}

export function seek(timeSeconds: number): void {
  playbackClock.time = Math.min(playbackClock.duration, Math.max(0, timeSeconds));
  emitPlaybackChange();
}
